import { Component, inject } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { map, Subject, Subscription, switchMap, firstValueFrom } from "rxjs";
import { environment } from "../../../environments/environment";
import { SocketService } from "../../core/event/services/socket.service";
import { AlertService } from "../alert/services/alert.service";
import { AlertQueueService, AlertPlaybackControl } from "../alert/services/alert-queue.service";
import { AlertPlaybackOrchestrator } from "../alert/services/alert-playback-orchestrator.service";
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from "@angular/animations";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { AlertDisplayComponent } from "../alert/components/alert-display/alert-display.component";
import { HybridTTSService } from "../../core/services/hybrid-tts.service"; // 🎤 Import HybridTTSService
import { DatadogService } from "../../core/observability/datadog.service";
import { v4 as uuidv4 } from "uuid";
import {
  HEARTBEAT_INTERVAL_MS,
  ALERT_DEFAULT_DURATION_MS,
  ALERT_AUTOHIDE_BUFFER_MS,
  ALERT_TIMEOUT_MS,
  ALERT_GUARD_MS,
} from "../alert/services/alert-constants";

enum EventType {
  DONATION = "donation",
  SALE = "sale",
  ALERT_SETTINGS_UPDATED = "alert_settings_updated",
  STOP_ALERT = "stop_alert",
  RELOAD_PAGE = "reload_page",
}

@Component({
  standalone: true,
  selector: "app-sale",
  templateUrl: "./sale.component.html",
  styleUrls: ["./sale.component.scss"],
  imports: [CommonModule, FormsModule, AlertDisplayComponent, MatIconModule],
  providers: [SocketService, HybridTTSService],
  animations: [
    trigger("fadeInOut", [
      state("void", style({ opacity: 0 })),
      transition(":enter", [animate("300ms ease-in", style({ opacity: 1 }))]),
      transition(":leave", [animate("300ms ease-out", style({ opacity: 0 }))]),
    ]),
  ],
})
export class SaleComponent {
  public event: any = null;
  public showMessage = false;
  public message: string = "";

  // Parâmetros da URL
  public hashSales!: string;
  public streamerId: string = "";
  public paramSubscriptions: Subscription[] = [];

  // Serviços
  alertService = inject(AlertService);
  alertQueueService = inject(AlertQueueService);
  alertAudioOrchestrator = inject(AlertPlaybackOrchestrator);
  socketService = inject(SocketService);
  activatedRoute = inject(ActivatedRoute);
  titleService = inject(Title);
  hybridTTSService = inject(HybridTTSService); // 🎤 Inject HybridTTSService
  datadogService = inject(DatadogService);
  // helper para ACK
  private sendAck(alertId: string, status: "done" | "failed", reason?: string) {
    // 👁️ Não enviar ACK no modo visualização
    if (this.watchMode) {
      console.log(`ℹ️ [Sale] WATCH MODE - ACK suprimido para ${alertId} (${status})`);
      return;
    }

    this.socketService.emitWithAck("ack_alert", { alertId, status, reason }).subscribe({
      next: () => {},
      error: (err) => {
        console.error("[Overlay][sale] ACK via WS falhou, tentando emit simples", err);
        this.socketService.emit("ack_alert", { alertId, status, reason } as any);
      },
    });
  }

  // TTS - manter para compatibilidade mas usar HybridTTSService como primário
  private speechSynthesis: SpeechSynthesis | null = null;
  private currentVoice: SpeechSynthesisVoice | null = null;

  // Flags
  public isSale = false;
  public isLocal = false;
  public alertPosition: "top" | "center" | "bottom" = "center";
  public watchMode = false; // 👁️ Modo visualização (não envia ACK)

  // Armazenar evento atual para TTS
  private currentEventForTTS: any = null;
  
  // 🎯 Subject RxJS que emite quando coin.wav + TTS terminarem
  private audioFinished$: Subject<void> | null = null;
  private stopQueueSub?: Subscription;
  private stopRequested = false;
  private heartbeatTimer?: any;
  private socketHealthSubs: Subscription[] = [];
  private displayId: string = this.getOrCreateDisplayId();
  
  // 🔊 Volume do alerta (será preenchido pelas configurações)
  public alertVolume: number = 70; // Default 70%
  public stopSignal = 0; // usado para forçar parada do áudio coin.wav
  public voiceRecordingUrl: string | null = null; // URL do áudio customizado
  private alertsPaused = false;

  constructor() {}

  ngOnInit(): void {
    // 👁️ Detectar modo visualização via route data
    this.activatedRoute.data.subscribe(data => {
      this.watchMode = data['watchMode'] || false;
      if (this.watchMode) {
        console.log('👁️ [Sale] MODO VISUALIZAÇÃO - Esperando broadcasts');
      }
    });

    this.initializeParams();
    this.initializeTTS();
    this.setupSocketHealthListeners();
    this.connectToSocket();
    
    // 🎯 Normal mode: processa fila | Watch mode: escuta broadcasts
    if (this.watchMode) {
      this.subscribeToBroadcasts();
    } else {
      this.subscribeToAlertQueue();
    }
    
    this.stopQueueSub = this.alertQueueService.stopSignal$.subscribe(() => {
      this.stopCurrentPlayback();
    });
  }

  private startHeartbeat(): void {
    if (!this.streamerId) return;
    clearInterval(this.heartbeatTimer);
    this.socketService
      .emitWithAck("display_heartbeat", {
        streamerId: this.streamerId,
        displayId: this.displayId,
        setActive: true,
      })
      .subscribe();
    this.heartbeatTimer = setInterval(() => {
      this.socketService.emitWithAck("display_heartbeat", {
        streamerId: this.streamerId,
        displayId: this.displayId,
      }).subscribe();
    }, HEARTBEAT_INTERVAL_MS);
  }

  private getOrCreateDisplayId(): string {
    const key = "alert_display_id";
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = uuidv4();
    localStorage.setItem(key, id);
    return id;
  }

  /**
   * 📥 Escuta fila centralizada de alertas (NORMAL MODE)
   */
  private subscribeToAlertQueue(): void {
    this.alertQueueService.alertPlayback$.subscribe(({ event, control }) => {
      if (!this.alertQueueService.isCurrentGeneration(event)) {
        control.complete();
        return;
      }
      // Filtrar apenas eventos do tipo SALE
      if (event.type === EventType.SALE) {
        this.playAlert(event, control);
      }
    });
  }

  /**
   * 👁️ Escuta broadcasts de displays normais (WATCH MODE)
   */
  private subscribeToBroadcasts(): void {
    this.socketService.onEvent('alert_display_broadcast').subscribe((payload: any) => {
      console.log('📺 [Sale Watch] Broadcast recebido', payload);
      
      // Reconstruir evento a partir do broadcast
      const event = {
        id: payload.alertId,
        type: EventType.SALE,
        data: payload.alertData,
        timestamp: payload.timestamp,
      };
      
      // Criar control mock (watch não envia ACK)
      const mockControl: AlertPlaybackControl = {
        complete: () => {
          console.log('👁️ [Sale Watch] Playback completo (mock)');
          this.showMessage = false;
          this.event = null;
        },
        skip: () => {
          console.log('👁️ [Sale Watch] Skip chamado (mock)');
          this.showMessage = false;
          this.event = null;
        },
      };
      
      this.playAlert(event, mockControl);
    });
  }

  ngOnDestroy(): void {
    this.stopCurrentPlayback(true);
    this.stopQueueSub?.unsubscribe();
    this.paramSubscriptions.forEach((sub) => sub.unsubscribe());
    this.socketHealthSubs.forEach((sub) => sub.unsubscribe());
    this.socketService.disconnect();
    clearInterval(this.heartbeatTimer);
  }

  private initializeParams(): void {
    this.isLocal = environment.stage === "local";
    const paramSub = this.activatedRoute.params
      .pipe(
        switchMap((params: any) => {
          this.hashSales = params?.id;
          this.titleService.setTitle("The Cool Rhyno - Sales");
          return this.activatedRoute.queryParams;
        }),
        map((queryParams: any) => {

          return queryParams;
        })
      )
      .subscribe();

    this.paramSubscriptions.push(paramSub);
  }

  private setupSocketHealthListeners(): void {
    this.socketHealthSubs.push(
      this.socketService.onConnected$.subscribe(() => {
        this.alertQueueService.resume();
      })
    );
    this.socketHealthSubs.push(
      this.socketService.onDisconnected$.subscribe((reason) => {
        this.alertQueueService.pause();
        this.stopCurrentPlayback(true);
        this.datadogService.trackAction("socket_disconnected", {
          reason,
          type: "sale",
        });
      })
    );
  }

  private initializeTTS(): void {
    if ("speechSynthesis" in window) {
      this.speechSynthesis = window.speechSynthesis;

      const loadVoices = () => {
        const voices = this.speechSynthesis!.getVoices();

        const settings = this.alertService.getDefaultSettings();

        // Tentar encontrar a voz específica selecionada pelo usuário
        this.currentVoice =
          voices.find((voice) => voice.voiceURI === settings.speechVoice) ||
          voices.find((voice) => voice.lang.startsWith("pt")) ||
          voices[0] ||
          null;
      };

      if (this.speechSynthesis.getVoices().length > 0) {
        loadVoices();
      } else {
        this.speechSynthesis.addEventListener("voiceschanged", loadVoices);
      }
    }
  }

  private connectToSocket(): void {
    if (this.hashSales) {

      this.socketService.connect();


      this.socketService
        .emitWithAck("get_streamer_by_hash", { hash: this.hashSales })
        .subscribe((response: any) => {
          if (response && response.data) {
            this.streamerId = response.data.id;
            this.alertService.loadConfigFromSocket(response.data.alertSettings);
            
            // 👁️ Watch mode não deve ser considerado display ativo
            if (!this.watchMode) {
              this.startHeartbeat();
              this.socketService
                .emitWithAck("display_ready", {
                  streamerId: this.streamerId,
                  displayId: this.displayId,
                })
                .subscribe();
              console.info("[Overlay][sale] display_ready sent", {
                streamerId: this.streamerId,
                displayId: this.displayId,
              });
            } else {
              console.info("[Overlay][sale] 👁️ Watch mode - Sem display_ready/heartbeat", {
                streamerId: this.streamerId,
              });
            }
            
            this.alertQueueService.resume();
            this.syncAlertsPausedFromSettings(
              response.data.alertSettings,
              "initial_connect"
            );
            this.datadogService.init(this.streamerId);
            this.datadogService.setViewName(`sales/${this.streamerId || this.hashSales}`);
            
            this.socketService
              .onEvent(`event:${this.streamerId}`)
              .subscribe((eventPayload: any) => {
                if (eventPayload.type === EventType.ALERT_SETTINGS_UPDATED) {
                  this.getStreamerIdFromHash(this.hashSales, "settings_updated");
                } else if (eventPayload.type === EventType.RELOAD_PAGE) {
                  window.location.reload();
                } else if (eventPayload.type === EventType.STOP_ALERT) {
                  this.stopCurrentPlayback(true, undefined, true);
                }
              });

            this.socketService
              .onEvent(`streamer:${this.streamerId}:play_alert`)
              .subscribe((payload: any) => {
                console.info("[Overlay][sale] play_alert recebido", {
                  alertId: payload?.alertId,
                  type: payload?.type,
                  targetDisplayId: payload?.targetDisplayId,
                  forThisDisplay:
                    !payload?.targetDisplayId ||
                    payload?.targetDisplayId === this.displayId,
                });
                this.handlePlayAlert(payload);
              });

            // 🛑 Listener para STOP_ALERT (skip-current do admin)
            this.socketService
              .onEvent(`streamer:${this.streamerId}:stop_alert`)
              .subscribe((payload: any) => {
                console.info("🚨🚨🚨 [Overlay][sale] stop_alert recebido 🚨🚨🚨", {
                  alertId: payload?.alertId,
                  clearQueue: payload?.clearQueue,
                  timestamp: new Date().toISOString(),
                });
                // Para imediatamente o alerta atual sem ACK; backend já marcou como skipped
                this.stopCurrentPlayback(true, undefined, true);
                console.info("✅ [Overlay][sale] stopCurrentPlayback executado");
                
                if (payload?.clearQueue) {
                  this.alertQueueService.clearAll('backend_stop_with_clear');
                }
              });
          }
        });
    }
  }

  private getStreamerIdFromHash(hash: string, source: string = "manual"): void {
    this.socketService
      .emitWithAck("get_streamer_by_hash", { hash })
      .subscribe((response: any) => {
        if (response && response.data) {
          this.alertService.loadConfigFromSocket(response.data.alertSettings);
          this.syncAlertsPausedFromSettings(
            response.data.alertSettings,
            `settings_fetch:${source}`
          );
        }
      });
  }

  private syncAlertsPausedFromSettings(
    alertSettings: any[],
    source: string
  ): void {
    const saleSettings = alertSettings?.find(
      (s: any) => s.alertType === "SALE"
    );
    if (typeof saleSettings?.alertsPaused === "boolean") {
      this.applyAlertsPaused(saleSettings.alertsPaused, source);
    }
    // Atualizar voiceRecordingUrl
    this.updateVoiceRecordingUrl();
  }

  private updateVoiceRecordingUrl(): void {
    const settings = this.alertService.getSettings("sale");
    this.voiceRecordingUrl = settings.voiceRecordingUrl || null;
  }

  private applyAlertsPaused(paused: boolean, source: string): void {
    if (paused === this.alertsPaused) return;
    this.alertsPaused = paused;
    if (paused) {
      this.alertQueueService.pause();
      this.stopCurrentPlayback();
      this.alertQueueService.cancelCurrent();
      this.datadogService.trackAction("alerts_paused", {
        source,
        type: "sale",
      });
    } else {
      this.alertQueueService.resume();
      this.datadogService.trackAction("alerts_resumed", {
        source,
        type: "sale",
      });
    }
  }

  private handlePlayAlert(payload: any): void {
    if (!payload || !payload.alertId) {
      console.debug("[Overlay][sale] play_alert sem alertId, ignorando", payload);
      return;
    }
    const type = String(payload.type || "").toLowerCase();
    if (type !== EventType.SALE) {
      console.debug("[Overlay][sale] Ignorando play_alert: tipo diferente", payload);
      return;
    }
    if (payload.targetDisplayId && payload.targetDisplayId !== this.displayId) {
      console.debug("[Overlay][sale] Ignorando play_alert: targetDisplayId não corresponde", {
        payloadTarget: payload.targetDisplayId,
        displayId: this.displayId,
      });
      return;
    }
    const data = payload.payload || payload.data;
    const settings = this.alertService.getSettings("sale");
    const total = data?.total ?? data?.amount;
    if (total !== undefined && total < (settings as any).minAmount) {
      this.datadogService.logInfo('[Sale] Alert ignored: below minAmount', {
        alertId: payload.alertId,
        total,
        minAmount: (settings as any).minAmount,
      });
      this.sendAck(payload.alertId, "done");
      console.info("[Overlay][sale] Ignorando por minAmount, ack done", {
        alertId: payload.alertId,
        total,
      });
      return;
    }
    this.datadogService.logInfo('[Sale] Alert received and enqueueing', {
      alertId: payload.alertId,
      type,
      priority: payload.priority,
      total,
    });
    console.info("[Overlay][sale] Enfileirando alerta", {
      alertId: payload.alertId,
      type,
      priority: payload.priority,
    });
    this.alertQueueService.enqueue({
      id: payload.alertId,
      type: EventType.SALE,
      data,
      priority: payload.priority, // High priority (>= 999) bypasses pause
    });
  }

  /**
   * 🎬 Processa alerta individual (chamado pela fila)
   */
  private async playAlert(
    event: any,
    control: AlertPlaybackControl
  ): Promise<void> {

    // �🔄 Resetar flag de stop para permitir que este alerta toque
    this.hybridTTSService.resetStopFlag();
    
    this.stopRequested = false;
    let ackSent = false;
    let autoHideTimer: any;
    try {
      const settings = this.alertService.getSettings(event.type);
      
      // Configurar dados do alerta
      this.event = event;
      this.currentEventForTTS = event;
      this.isSale = event.type === EventType.SALE;
      this.alertVolume = (settings.speechVolume || 0.8) * 100;
      this.alertPosition = settings.alertPosition;

      // 🔄 Criar Subject específico para este alerta
      this.audioFinished$ = new Subject<void>();
      const audioFinished$ = this.audioFinished$;
      
      // Gerar template visual
      this.message = this.replacePlaceHoldersSale(
        settings.template,
        event.data
      );
      
      // Mostrar alerta
      this.showMessage = true;

      // 📺 Broadcast para watch displays DEPOIS de exibir (apenas se não estiver em watch mode)
      if (!this.watchMode && this.streamerId) {
        this.socketService.emitWithAck('broadcast_alert_display', {
          streamerId: this.streamerId,
          alertId: event.id,
          alertData: event.data,
        }).subscribe({
          next: () => console.log('📺 [Sale] Broadcast enviado para watches'),
          error: (err) => console.error('❌ [Sale] Erro ao enviar broadcast', err),
        });
      }

      this.datadogService.trackAction("alert_start", {
        eventId: event.id,
        type: event.type,
        streamerId: this.streamerId,
      });

      // Fallback de auto-hide para evitar travar UI se áudio falhar/autoplay bloquear
      const configuredDurationMs = (settings as any).alertDuration
        ? Number((settings as any).alertDuration) * 1000
        : 0;
      const baseDuration = Math.max(
        configuredDurationMs,
        ALERT_TIMEOUT_MS
      );
      const maxMs = baseDuration + ALERT_AUTOHIDE_BUFFER_MS;
      autoHideTimer = setTimeout(() => {
        if (ackSent) return;
        this.stopRequested = true;
        this.showMessage = false;
        this.event = null;
        this.currentEventForTTS = null;
        this.isSale = false;
        this.audioFinished$ = null;
        control.complete();
        this.sendAck(event.id, "done");
        ackSent = true;
      }, maxMs);
      
      // ✅ Orquestrar coin + TTS + timeout (igual donate)
      await this.alertAudioOrchestrator.runFlow({
        eventId: event.id,
        waitForCoin$: audioFinished$,
        guardMs: ALERT_GUARD_MS,
        timeoutMs: ALERT_TIMEOUT_MS,
        onTimeout: () => this.stopCurrentPlayback(false, event.id),
        playTTS: async () => {
          if (this.stopRequested) return;
          await this.playAudioForSale(event);
        },
      });
      if (this.stopRequested) {
        clearTimeout(autoHideTimer);
        control.complete();
        return;
      }
      
      // Esconder e limpar
      this.showMessage = false;
      this.event = null;
      this.currentEventForTTS = null;
      this.isSale = false;
      this.audioFinished$ = null;
      this.datadogService.trackAction("alert_finished", {
        eventId: event.id,
        type: event.type,
        streamerId: this.streamerId,
      });
      
      control.complete();
      this.sendAck(event.id, "done");
      ackSent = true;
    } catch (error) {
      console.error("❌ Erro ao processar alerta de venda:", error);
      this.datadogService.logError("alert_playback_error", {
        eventId: event.id,
        type: event.type,
        streamerId: this.streamerId,
        error: String(error),
      });
      
      this.stopRequested = true;
      this.showMessage = false;
      this.event = null;
      this.currentEventForTTS = null;
      this.isSale = false;
      this.audioFinished$ = null;
      control.complete();
      
      // ❌ Enviar como "failed" para poder retry depois
      this.sendAck(event.id, "failed", "audio_error");
      ackSent = true;
    } finally {
      if (ackSent) {
        clearTimeout(autoHideTimer);
      }
    }
  }

  private async playTTS(event: any): Promise<void> {
    const settings = this.alertService.getSettings(event.type);

    if (!settings.speechEnabled) {
      return;
    }

    // 🔄 Usar TTS nativo direto (fallback)
    // Backend já tentou gerar AI, então aqui é só fallback
    await this.playNativeTTS(event, settings);
  }

  /**
   * 🔄 Fallback TTS nativo (mantido por compatibilidade)
   */
  private async playNativeTTS(event: any, settings: any): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.speechSynthesis) {

        const voices = this.speechSynthesis.getVoices();


        const selectedVoice =
          voices.find((voice) => voice.voiceURI === settings.speechVoice) ||
          voices.find((voice) => voice.lang.startsWith("pt")) ||
          voices[0] ||
          null;



        const utterance = new SpeechSynthesisUtterance(this.message);
        utterance.lang = selectedVoice?.lang || "pt-BR"; // 🔑 garantir idioma
        utterance.voice = selectedVoice;
        utterance.rate = 1.0; // 🎯 VELOCIDADE FIXA (1.0x) para vendas
        utterance.pitch = settings.speechPitch;
        // 🔊 Usar volume configurado (settings.speechVolume já está 0-100, converter para 0-1)
        utterance.volume = settings.speechVolume / 100;

        // ✅ Resolver Promise quando TTS terminar
        utterance.onend = () => resolve();
        utterance.onerror = () => {
          console.warn('⚠️ TTS error, resolving anyway');
          resolve();
        };

        this.speechSynthesis.cancel(); // 🔑 limpa fila antes
        this.speechSynthesis.speak(utterance);

      } else {
        resolve();
      }
    });
  }

  /**
   * 🎯 Template FIXO: {username} comprou {quantity} {product}
   * Sempre usa o template fixo (idêntico ao TTS do backend)
   */
  private replacePlaceHoldersSale(template: string, data: any) {
    // 🔒 SEMPRE usar template fixo (igual ao TTS)
    const FIXED_TEMPLATE = '{username} comprou {quantity} {product}';
    
    return FIXED_TEMPLATE
      .replace("{username}", data.username || "Anônimo")
      .replace("{quantity}", (data.quantity || 1).toString())
      .replace("{product}", data.productStreamerName || "produto");
  }

  private async playAudioForSale(event: any): Promise<void> {
    const saleId = event.data.id;
    const hasReplay = !!event.data.audioUrl;

    // 🎨 TESTE VISUAL: Se ID começa com "test-", não gerar áudio
    if (saleId?.startsWith('test-')) {
      this.audioFinished$?.next();
      return;
    }

    this.datadogService.trackAction("sale_audio_start", {
      eventId: event.id,
      saleId,
      hasReplay,
    });

    const settings = this.alertService.getSettings(event.type);

    try {
      if (hasReplay) {
        await firstValueFrom(
          this.hybridTTSService.playAIAudio(
            event.data.audioUrl,
            settings
          )
        );
        this.datadogService.trackAction("sale_audio_replay_success", {
          eventId: event.id,
          saleId,
        });
      } else {
        await this.playTTS(event);
        this.datadogService.trackAction("sale_audio_tts_success", {
          eventId: event.id,
          saleId,
        });
      }
    } catch (error) {
      console.error('❌ Erro ao tocar áudio:', error);
      this.datadogService.trackAction("sale_audio_error", {
        eventId: event.id,
        saleId,
        error: String(error),
      });
      // Tentar fallback
      try {
        await this.playTTS(event);
        this.datadogService.trackAction("sale_audio_tts_success", {
          eventId: event.id,
          saleId,
          fallback: true,
        });
      } catch (fallbackError) {
        console.error('❌ Erro no fallback:', fallbackError);
        this.datadogService.trackAction("sale_audio_tts_failed", {
          eventId: event.id,
          saleId,
          error: String(fallbackError),
        });
      }
    }
  }

  public async onAudioFinished(): Promise<void> {
    // Apenas sinaliza que o coin.wav terminou; o orquestrador segue o fluxo de áudio/TTS.
    this.audioFinished$?.next();
  }

  /**
   * ⚡ Força parada do alerta atual (coin.wav + TTS) e, opcionalmente, limpa a fila.
   */
  public stopCurrentPlayback(
    clearQueue: boolean = false,
    targetEventId?: string,
    suppressAck: boolean = false
  ): void {
    console.log('🛑 [Sale] stopCurrentPlayback chamado', {
      clearQueue,
      targetEventId,
      suppressAck,
      currentEventId: this.currentEventForTTS?.id,
      hasSpeechSynthesis: !!this.speechSynthesis,
    });
    
    this.stopRequested = true;
    const currentId = this.currentEventForTTS?.id;
    // Se houver um targetEventId e o alerta atual já mudou ou não existe, não interrompa o novo alerta.
    if (targetEventId) {
      if (!this.currentEventForTTS || this.currentEventForTTS.id !== targetEventId) {
        console.log('⚠️ [Sale] targetEventId não corresponde, abortando stop');
        return;
      }
    }

    // Para áudio coin.wav no AlertDisplay
    this.stopSignal++;
    console.log('🔊 [Sale] stopSignal incrementado:', this.stopSignal);

    // Para TTS em andamento
    if (this.speechSynthesis) {
      console.log('🎬 [Sale] Cancelando speechSynthesis');
      this.speechSynthesis.cancel();
    }

    // Parar áudio da IA (hybridTTS)
    console.log('🤖 [Sale] Parando hybridTTSService');
    this.hybridTTSService.stopCurrentAudio();
    
    console.log('🎧 [Sale] Parando alertAudioOrchestrator');
    this.alertAudioOrchestrator.stop();

    // Emitir no Subject para liberar fila se estiver aguardando
    this.audioFinished$?.next();
    this.audioFinished$ = null;

    // Limpar estado atual do alerta
    this.showMessage = false;
    this.event = null;
    this.isSale = false;

    if (clearQueue) {
      this.alertQueueService.cancelCurrent();
    }

    if (!suppressAck && currentId) {
      this.sendAck(currentId, "failed");
    }
    this.currentEventForTTS = null;
  }

}
